import { RefObject, useCallback, useEffect, useState } from 'react';

/**
 * Fullscreen API over a container ref (spec 0010 — «показать доску на весь
 * экран» во время занятия).
 *
 * Vendor-prefixed fallbacks are here for Safari, which still ships only the
 * `webkit*` names. `isFullscreen` is driven by the browser event rather than
 * by our own click, so Esc / F11 keep the button label honest.
 */

interface IFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface IFullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

const getFullscreenElement = (): Element | null => {
  const doc = document as IFullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
};

export const useFullscreen = (ref: RefObject<HTMLElement>) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const sync = () => setIsFullscreen(getFullscreenElement() === ref.current);
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [ref]);

  const toggle = useCallback(() => {
    const doc = document as IFullscreenDocument;
    if (getFullscreenElement()) {
      (doc.exitFullscreen ?? doc.webkitExitFullscreen)?.call(doc);
      return;
    }
    const node = ref.current as IFullscreenElement | null;
    if (!node) {
      return;
    }
    (node.requestFullscreen ?? node.webkitRequestFullscreen)?.call(node);
  }, [ref]);

  const isSupported =
    typeof document !== 'undefined' &&
    Boolean(
      document.fullscreenEnabled ??
        (document as IFullscreenDocument & { webkitFullscreenEnabled?: boolean })
          .webkitFullscreenEnabled,
    );

  return { isFullscreen, toggle, isSupported };
};
